import { memo } from 'react';
import { cn } from '@/lib/cn';
import { SLOTH_ALT_TEXT, type SlothState } from './sloth-states';
import { resolveSlothSequence, STATIC_PLAYBACK, type SlothAssetSize } from './sloth-assets';
import { useSlothFrame } from './use-sloth-frame';
import { SlothPlaceholder } from './sloth-placeholder';

/**
 * 所有樹懶圖片的唯一出口（spec 22.2、22.3、deployment.md 第 4 節）。
 *
 * 呼叫端只給狀態，不知道也不需要知道背後是一張圖還是好幾格：
 * 素材由 `sloth-assets.ts` 依資料夾解析，換格由 `use-sloth-frame.ts` 排程。
 * 之後要改成 Rive、Lottie 或影片，仍然只改這一個元件。
 *
 * 多格時所有格會同時掛在 DOM 上互相疊著，只切 opacity：
 * 一來瀏覽器一開始就把每一格載完，換格不會閃爍；
 * 二來符合 spec 23.1「只動 transform 與 opacity」。
 */
export interface SlothAssetSlotProps {
  state: SlothState;
  /** 純裝飾時不進入無障礙樹。 */
  decorative?: boolean;
  /** `sm` 給 ≤48px 的收合場合，會優先挑半身素材。預設 `md`。 */
  size?: SlothAssetSize;
  className?: string;
}

/** 換格用的柔和淡入淡出；reduced motion 由 index.css 的全域規則壓到 0.01ms。 */
const CROSSFADE_MS = 320;

export const SlothAssetSlot = memo(function SlothAssetSlot({
  state,
  decorative = false,
  size = 'md',
  className,
}: SlothAssetSlotProps) {
  const sequence = resolveSlothSequence(state, size);
  const frameIndex = useSlothFrame(
    sequence?.frames.length ?? 0,
    sequence?.playback ?? STATIC_PLAYBACK,
  );

  const accessibility = decorative
    ? ({ 'aria-hidden': true } as const)
    : ({ role: 'img', 'aria-label': SLOTH_ALT_TEXT[state] } as const);

  if (!sequence) {
    return (
      <div {...accessibility} data-sloth-state={state} className={className}>
        <SlothPlaceholder state={state} />
      </div>
    );
  }

  return (
    <div
      {...accessibility}
      data-sloth-state={state}
      data-sloth-slot={sequence.slot}
      data-sloth-frame={sequence.frames[frameIndex]?.id}
      className={cn(
        'relative w-full select-none',
        size === 'sm' ? 'aspect-square' : 'aspect-[4/5]',
        className,
      )}
    >
      {sequence.frames.map((frame, index) => (
        <img
          key={frame.id}
          src={frame.src}
          srcSet={frame.srcSet}
          alt=""
          aria-hidden="true"
          draggable={false}
          decoding="async"
          className="absolute inset-0 h-full w-full object-contain"
          style={{
            opacity: index === frameIndex ? 1 : 0,
            transition: `opacity ${CROSSFADE_MS}ms ease-in-out`,
          }}
        />
      ))}
    </div>
  );
});
